import { RepairOrderQuoteItems } from '../form-state';
import { CollapseBtnComponent } from './CollapseBtn';
import { ActionButtons } from './action-buttons';
import { ButtonsGroup } from './buttons-group';
import { LaboursTable } from './labours-table';
import { NotFoundComponent } from './not-found';
import { PartsTable } from './parts-table';
import { useRepairOrderQuotesStore } from './repair-order-quotes-context';
import SelectDateModal from './select-date-modal';
import styles from './styles.module.scss';
import { yupResolver } from '@hookform/resolvers/yup';
import { RepairOrderQuoteStatusEnum } from 'api-new/generated';
import { VHCRecordCheckResultDto } from 'api/generated';
import clsx from 'clsx';
import { Loading } from 'components/loading';
import { RepairOrderQuoteStatusEnumValues } from 'entities/repair-order-quote';
import { useAuth } from 'features/auth/contexts/auth';
import { useCommonRepairOrderJobFromQuoteMutation } from 'features/vhc/api/use-common-repair-order-job-from-quote.mutation';
import { useCommonRepairOrderQuoteStatusMutation } from 'features/vhc/api/use-common-repair-order-quote-status.mutation';
import { useCommonRepairOrderQuotesMutation } from 'features/vhc/api/use-common-repair-order-quotes.mutation';
import { useCommonRepairOrderQuotes } from 'features/vhc/api/use-common-repair-order-quotes.query';
import { useContactsDialogsEmailMutation } from 'features/vhc/api/use-contacts-Dialogs-email.mutation';
import { useContactsInitContactDialogMutation } from 'features/vhc/api/use-contacts-init-contact-dialog.mutation';
import { useDeleteCommonRepairOrderQuoteLabourMutation } from 'features/vhc/api/use-delete-common-repair-order-quote-labour.mutation';
import { useDeleteCommonRepairOrderQuotePartMutation } from 'features/vhc/api/use-delete-common-repair-order-quote-part.mutation';
import { useGetEmailByTemplateMutation } from 'features/vhc/api/use-get-email-by-template.mutation';
import { useWorkshopByIdQuery } from 'features/vhc/api/use-workshop-by-Id.query';
import { RepairOrderJobLinePayerValues, RepairOrderJobServiceTypeValues } from 'features/vhc/model';
import useToggle from 'hooks/use-toggle';
import { FC, Fragment, useEffect, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { formatCurrencySafe, formatDate } from 'utils/formats';
import * as yup from 'yup';

const isMultipleOf005 = (value: number): boolean => {
  const normalized = Math.round((value + Number.EPSILON) * 100);
  return normalized % 5 === 0;
};

const schema = yup.object().shape({
  items: yup.array().of(
    yup.object().shape({
      labor: yup.array().of(
        yup.object().shape({
          isDeleted: yup.boolean().default(false),

          description: yup.string().when('isDeleted', {
            is: true,
            then: (schema) => schema.notRequired(),
            otherwise: (schema) => schema.required('Description is required')
          }),

          laborCode: yup.string().when('isDeleted', {
            is: true,
            then: (schema) => schema.notRequired(),
            otherwise: (schema) => schema.required('Labor code is required')
          }),

          allowedTimeInMinutes: yup.number().when('isDeleted', {
            is: true,
            then: (schema) => schema.notRequired(),
            otherwise: (schema) =>
              schema
                .typeError('Value must be a number')
                .required('Time is required')
                .test(
                  'multiple-of-0.05',
                  'Time must be a multiple of 3 minutes (example: 6 minutes - 0.1, 15 minutes - 0.25, 30 minutes - 0.5, 60 minutes - 1)',
                  function (value) {
                    if (typeof value !== 'number') return false;
                    return isMultipleOf005(value / 60);
                  }
                )
          })
        })
      ),
      parts: yup.array().of(
        yup.object().shape({
          isDeleted: yup.boolean().default(false),

          brandCode: yup.string().when('isDeleted', {
            is: true,
            then: (schema) => schema.notRequired(),
            otherwise: (schema) => schema.required('Brand code is required')
          }),

          partNumber: yup.string().when('isDeleted', {
            is: true,
            then: (schema) => schema.notRequired(),
            otherwise: (schema) => schema.required('Part number is required')
          }),

          description: yup.string().when('isDeleted', {
            is: true,
            then: (schema) => schema.notRequired(),
            otherwise: (schema) => schema.required('Description is required')
          }),

          price: yup.number().when('isDeleted', {
            is: true,
            then: (schema) => schema.notRequired(),
            otherwise: (schema) =>
              schema
                .required('Price is required')
                .typeError('Price must be a number')
                .moreThan(0, 'Price must be greater than 0')
          }),

          quantity: yup.number().when('isDeleted', {
            is: true,
            then: (schema) => schema.notRequired(),
            otherwise: (schema) =>
              schema
                .typeError('Quantity must be a number')
                .required('Quantity is required')
                .moreThan(0, 'Quantity must be greater than 0')
          })
        })
      )
    })
  )
});

type Props = {
  checkResult: Array<VHCRecordCheckResultDto>;
  soHeaderId?: number;
  branchId?: number;
  regNo?: string;
  dateCompleted?: string | null;
  dateSentAuthorizationRequiredEmail?: string | null;
  userSentAuthorizationRequiredEmail?: string | null;
  dateSentAuthorizationRequiredSms?: string | null;
  userSentAuthorizationRequiredSms?: string | null;
  refetchVhcRecord?: () => void;
};
export const RepairOrderQuotesTab: FC<Props> = ({
  soHeaderId,
  branchId,
  regNo,
  checkResult,
  dateCompleted,
  dateSentAuthorizationRequiredEmail,
  userSentAuthorizationRequiredEmail,
  dateSentAuthorizationRequiredSms,
  userSentAuthorizationRequiredSms,
  refetchVhcRecord
}) => {
  const { id } = useParams();

  const [openedRows, setOpenedRows] = useState<number[]>([]);
  const {
    isEditMode,
    setIsEditMode,
    loadingCount,
    increaseLoading,
    decreaseLoading,
    setInitState,
    dataState,
    setDataState
  } = useRepairOrderQuotesStore();

  const isLoading = loadingCount > 0;

  const openSelectDateModalToggleState = useToggle<number>(false);

  const { currentUser } = useAuth();
  const { mutate: changeStatusMutate, isLoading: isLoadingStatus } = useCommonRepairOrderQuoteStatusMutation();
  const [hasItems, setHasItems] = useState(false);
  const formMethods = useForm<RepairOrderQuoteItems>({
    resolver: yupResolver(schema),
    defaultValues: {
      items: [
        {
          labor: []
        }
      ]
    },
    mode: 'onBlur'
  });

  const formValues = formMethods.watch('items');

  useEffect(() => {
    if (!Array.isArray(formValues)) return;

    formValues.forEach((item, index) => {
      const parts = item.parts ?? [];
      const labor = item.labor ?? [];

      if (parts.length === 0 && labor.length === 0) {
        setValue(`items.${index}.isDeleted`, false);
        return;
      }

      const allPartsDeleted = parts.length === 0 || parts.every((x) => x.isDeleted);
      const allLaborDeleted = labor.length === 0 || labor.every((x) => x.isDeleted);

      setValue(`items.${index}.isDeleted`, allPartsDeleted && allLaborDeleted);
    });
  }, [formValues]);

  useEffect(() => {
    setInitState({
      branch: branchId,
      soHeaderId: soHeaderId
    });
  }, [soHeaderId, branchId, setInitState]);

  const { reset: resetFormState, control, register, setValue, getValues, watch } = formMethods;

  const { isFetching, refetch } = useCommonRepairOrderQuotes(soHeaderId!, undefined, {
    enabled: !!soHeaderId,
    onSuccess: (data) => {
      if (!data) {
        resetFormState({});
        setHasItems(false);
        return;
      }

      setHasItems(true);
      setDataState(data);

      resetFormState({
        items: data.map((x) => ({
          id: x.id,
          description: x.description,
          serviceType: x.serviceType ?? undefined,
          linePayer: x.linePayer ?? undefined,
          status: x.status,
          labor: x.labours ?? [],
          parts: x.parts?.map((z) => ({ ...z, isLookUped: z.isLookUped ?? false })),
          isSubmittedToCDK: x.isSubmittedToCDK
        }))
      });
    }
  });

  useEffect(() => {}, []);

  const { mutate: saveAllMutate, isLoading: isLoadingSave } = useCommonRepairOrderQuotesMutation();
  const { mutate: deleteLabourMutate } = useDeleteCommonRepairOrderQuoteLabourMutation();
  const { mutate: deletePartMutate } = useDeleteCommonRepairOrderQuotePartMutation();

  const onSubmit = (values: RepairOrderQuoteItems) => {
    setIsEditMode(false);
    for (const value of values.items ?? []) {
      value.soHeaderId = soHeaderId;
      value.branchId = branchId;

      const currentData = dataState?.find((x) => x.id == value.id);

      if (currentData) {
        const laborIds = value.labor?.filter((x) => x.needDelete && x.id).map((x) => x.id) ?? [];
        const partsIds = value.parts?.filter((x) => x.needDelete && x.id).map((x) => x.id) ?? [];

        for (const id of laborIds) {
          deleteLabourMutate({ id: id!, userId: currentUser?.userId });
        }

        for (const id of partsIds) {
          deletePartMutate({ id: id!, userId: currentUser?.userId });
        }
      }

      if (!values?.items?.length) return;

      for (const item of values.items) {
        if (!item) continue;

        item.parts = item?.parts?.filter((x) => !x.isDeleted);
        item.labor = item?.labor?.filter((x) => !x.isDeleted);
      }

      saveAllMutate(
        {
          requestBody: { ...value }
        },
        {
          onSuccess: () => {
            setIsEditMode(false);
            refetch();
          }
        }
      );
    }
  };

  const { fields } = useFieldArray({
    control,
    name: 'items',
    keyName: 'ItemId'
  });

  const getCountLabours = (index: number) => {
    const labors = watch(`items.${index}.labor`) ?? [];

    return labors?.filter((x) => !x.isDeleted).length;
  };

  const getCountParts = (index: number) => {
    const parts = watch(`items.${index}.parts`) ?? [];

    return parts.filter((x) => !x.isDeleted).length;
  };

  const getSumMinutes = (index: number) => {
    const labors = watch(`items.${index}.labor`) ?? [];

    return labors
      .filter((x) => !x.isDeleted)
      .reduce((acc, x) => {
        if (!x.allowedTimeInMinutes) return acc;

        const allowedTimeInMinutes = Number(x.allowedTimeInMinutes);

        if (isNaN(allowedTimeInMinutes)) return acc;

        return acc + (x.allowedTimeInMinutes ?? 0);
      }, 0);
  };

  const getTotal = (index: number) => {
    const labors = watch(`items.${index}.labor`)?.filter((x) => !x.isDeleted) ?? [];
    const parts = watch(`items.${index}.parts`)?.filter((x) => !x.isDeleted) ?? [];
    const totalPriceLabor = labors
      .filter((x) => !x.isDeleted)
      .reduce((acc, x) => {
        if (!x.totalInclVat) return acc;

        const totalInclVat = Number(x.totalInclVat);

        if (isNaN(totalInclVat)) {
          return acc;
        }
        return acc + totalInclVat;
      }, 0);

    const totalPriceParts = parts
      ?.filter((x) => !x.isDeleted)
      ?.reduce((acc, x) => {
        if (!x.totalInclVat) return acc;

        const totalInclVat = Number(x.totalInclVat);

        if (isNaN(totalInclVat)) return acc;

        return acc + totalInclVat;
      }, 0);

    return totalPriceLabor + totalPriceParts;
  };

  const executeStatusChange = (
    id: number,
    index: number,
    newStatus: number,
    statusDateTo: string | undefined = undefined
  ) => {
    increaseLoading();

    changeStatusMutate(
      {
        requestBody: {
          id: id,
          status: newStatus,
          userId: currentUser?.userId,
          statusDateTo: statusDateTo
        }
      },
      {
        onSuccess: () => {
          setValue(`items.${index}.status`, newStatus);
          decreaseLoading();
        },
        onError: () => {
          decreaseLoading();
        }
      }
    );
  };

  const handleChangeStatus = (index: number, newStatus: number) => {
    const { id } = watch(`items.${index}`);
    if (!id || newStatus < 0) return;

    if (newStatus === 6) {
      openSelectDateModalToggleState.setData(index);
      openSelectDateModalToggleState.open();
    } else {
      executeStatusChange(id, index, newStatus);
    }
  };

  const handleDateSelected = (index: number, statusDateTo: string) => {
    const { id } = watch(`items.${index}`);
    if (!id || !statusDateTo) return;
    executeStatusChange(id, index, 6, statusDateTo);
  };

  const { mutate: mutateCdk, isLoading: isLoadingCdk } = useCommonRepairOrderJobFromQuoteMutation();

  const handleSubmitCDK = (id: number, index: number) => {
    mutateCdk(
      {
        id,
        userId: currentUser?.userId
      },
      {
        onSettled: (data) => {
          setValue(`items.${index}.isSubmittedToCDK`, data);
        }
      }
    );
  };

  const { isLoading: isLoadingWorkshop } = useWorkshopByIdQuery({ soHeaderId: soHeaderId }, undefined, {
    enabled: false
  });
  const { isLoading: isLoadingContactsDialogsEmail } = useContactsDialogsEmailMutation();
  const { isLoading: isLoadingContactsEmailTemplate } = useGetEmailByTemplateMutation();

  const { isLoading: isLoadingContactsInit } = useContactsInitContactDialogMutation();

  const disablesSendCustomerEmail =
    isLoadingWorkshop || isLoadingContactsDialogsEmail || isLoadingContactsEmailTemplate || isLoadingContactsInit;

  useEffect(() => {
    if (disablesSendCustomerEmail) {
      increaseLoading();
    } else {
      decreaseLoading();
    }
  }, [disablesSendCustomerEmail]);

  const getStatusStyle = (val: RepairOrderQuoteStatusEnum): string => {
    if (val == RepairOrderQuoteStatusEnum.Created) {
      return styles.statusCreated;
    }

    if (val == RepairOrderQuoteStatusEnum.Authorised) {
      return styles.statusAuthorised;
    }

    if (val == RepairOrderQuoteStatusEnum.Sent) {
      return styles.statusSent;
    }

    if (val == RepairOrderQuoteStatusEnum.Confirmed) {
      return styles.statusConfirmed;
    }

    if (val == RepairOrderQuoteStatusEnum.Declined) {
      return styles.statusDeclined;
    }

    if (val == RepairOrderQuoteStatusEnum.Cancelled) {
      return styles.statusCancelled;
    }

    return '';
  };

  const canSubmitToCdk = (val?: RepairOrderQuoteStatusEnum) => {
    return [RepairOrderQuoteStatusEnum.Authorised, RepairOrderQuoteStatusEnum.Confirmed].some((x) => x === val);
  };

  const isHiddenStatus = (val: RepairOrderQuoteStatusEnum) => {
    if (val == RepairOrderQuoteStatusEnum.Sent) return true;

    return false;
  };

  const hasStateError = (index: number) => !!formMethods.formState.errors.items?.[index];

  const handleSaveQuote = async (index: number) => {
    const isValidForm = await formMethods.trigger(`items.${index}`);
    if (!isValidForm || hasStateError(index)) {
      return;
    }

    const item = formMethods.getValues(`items.${index}`);

    if (!item) return;

    const currentData = dataState?.find((x) => x.id == item.id);

    if (currentData) {
      const laborIds = item.labor?.filter((x) => x.needDelete && x.id).map((x) => x.id) ?? [];
      const partsIds = item.parts?.filter((x) => x.needDelete && x.id).map((x) => x.id) ?? [];

      for (const id of laborIds) {
        deleteLabourMutate({ id: id!, userId: currentUser?.userId });
      }

      for (const id of partsIds) {
        deletePartMutate({ id: id!, userId: currentUser?.userId });
      }
    }

    item.soHeaderId = soHeaderId;
    item.branchId = branchId;

    item.parts = item?.parts?.filter((x) => !x.isDeleted);
    item.labor = item?.labor?.filter((x) => !x.isDeleted);

    saveAllMutate(
      {
        requestBody: { ...item }
      },
      {
        onSuccess: () => {
          setIsEditMode(false);
          refetch();
        }
      }
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!isEditMode) {
      setIsEditMode(true);
      return;
    }
    formMethods.handleSubmit(onSubmit)();
  };

  const isDeletedRow = (rowIndex: number) => getValues(`items.${rowIndex}.isDeleted`);

  return (
    <>
      {openSelectDateModalToggleState.opened && (
        <SelectDateModal state={openSelectDateModalToggleState} onDateSelected={handleDateSelected} />
      )}

      <div className={clsx(!hasItems ? 'd-block' : 'd-none')}>
        <NotFoundComponent
          recordId={id ? Number(id) : undefined}
          refetch={refetch}
          soHeaderId={soHeaderId}
          regNo={regNo}
          checkResult={checkResult}
          dateCompleted={dateCompleted}
        />
      </div>

      <div className={clsx(hasItems ? 'd-block' : 'd-none')}>
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit}>
            <div className={styles.repairOrderContainer}>
              <div className={styles.actionBtn}>
                <ActionButtons />
              </div>
              <div
                style={{
                  width: '100%',
                  overflowX: 'auto',
                  overflowY: 'hidden'
                }}
              >
                <div className="table-responsive">
                  <table className={clsx('table', styles.table)}>
                    <thead>
                      <tr>
                        {/* <th>Source</th> */}
                        <th style={{ position: 'relative' }}>
                          <div>Description</div>
                        </th>
                        <th style={{ width: '130px', position: 'relative' }}>
                          <div>Service Type</div>
                        </th>
                        <th style={{ width: '150px', position: 'relative' }}>
                          <div>Payer</div>
                        </th>
                        <th style={{ width: '60px', position: 'relative' }}>
                          <div>Labours</div>
                        </th>
                        <th style={{ width: '60px', position: 'relative' }}>
                          <div>Parts</div>
                        </th>
                        <th style={{ width: '60px', position: 'relative' }}>
                          <div>Time</div>
                        </th>
                        <th style={{ minWidth: '80px', position: 'relative' }}>
                          <div className={clsx(styles.totalPriceIncVat)}>Total Price (incl VAT)</div>
                        </th>
                        <th style={{ width: '146px', position: 'relative' }}>
                          <div>Status</div>
                        </th>
                        <th style={{ width: '30px', position: 'relative' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {fields?.map((x, index) => (
                        <Fragment key={x.id}>
                          {/* Desktop */}
                          <tr className={clsx(styles.mainRow)}>
                            {/* <td></td> */}
                            <td>
                              <input
                                type="text"
                                className={clsx(
                                  'form-control form-control-solid',
                                  isDeletedRow(index) ? 'border-danger' : ''
                                )}
                                {...register(`items.${index}.description`)}
                                disabled={isDeletedRow(index) || !isEditMode}
                                style={{ ...(isDeletedRow(index) ? { backgroundColor: '#fff5f8' } : {}) }}
                              />
                            </td>
                            <td>
                              <select
                                className={clsx(
                                  'form-select form-select-solid',
                                  isDeletedRow(index) ? 'border-danger' : ''
                                )}
                                data-placeholder="Select option"
                                data-allow-clear="true"
                                data-hide-search="true"
                                style={{ ...(isDeletedRow(index) ? { backgroundColor: '#fff5f8' } : {}) }}
                                onChange={(e) =>
                                  setValue(
                                    `items.${index}.serviceType`,
                                    e.target.value ? Number(e.target.value) : undefined
                                  )
                                }
                                value={watch(`items.${index}.serviceType`) ?? ''}
                                disabled={isDeletedRow(index) || !isEditMode}
                              >
                                <option value="">Select</option>
                                {RepairOrderJobServiceTypeValues.map((x) => (
                                  <option key={x.value} value={x.value}>
                                    {x.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <select
                                className={clsx(
                                  'form-select form-select-solid',
                                  isDeletedRow(index) ? 'border-danger' : ''
                                )}
                                style={{ ...(isDeletedRow(index) ? { backgroundColor: '#fff5f8' } : {}) }}
                                data-placeholder="Select option"
                                data-allow-clear="true"
                                data-hide-search="true"
                                onChange={(e) =>
                                  setValue(
                                    `items.${index}.linePayer`,
                                    e.target.value ? Number(e.target.value) : undefined
                                  )
                                }
                                value={watch(`items.${index}.linePayer`) ?? ''}
                                disabled={!isEditMode}
                              >
                                <option value="">Select</option>
                                {RepairOrderJobLinePayerValues.map((x) => (
                                  <option key={x.value} value={x.value}>
                                    {x.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>{getCountLabours(index)}</td>
                            <td>{getCountParts(index)}</td>
                            <td>{Number(getSumMinutes(index) / 60).toFixed(2)}</td>
                            <td>{formatCurrencySafe(getTotal(index))}</td>
                            <td>
                              <select
                                className={clsx(
                                  'form-select form-select-solid',
                                  getStatusStyle(watch(`items.${index}.status`) ?? 0)
                                )}
                                data-placeholder="Select option"
                                data-allow-clear="true"
                                data-hide-search="true"
                                onChange={(e) =>
                                  handleChangeStatus(
                                    index,
                                    e.target.value !== undefined &&
                                      e.target.value !== null &&
                                      e.target.value.trim() !== ''
                                      ? Number(e.target.value)
                                      : -1
                                  )
                                }
                                value={watch(`items.${index}.status`) ?? ''}
                                disabled={isLoadingStatus || isLoading || isLoadingSave}
                              >
                                {RepairOrderQuoteStatusEnumValues.map((x) => (
                                  <option
                                    key={x.value}
                                    value={x.value}
                                    className={getStatusStyle(x.value)}
                                    hidden={isHiddenStatus(x.value)}
                                  >
                                    {x.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <CollapseBtnComponent
                                isShow={openedRows.some((z) => z == x.id)}
                                setIsShow={(val) => {
                                  const hasItemId = openedRows.some((z) => z == x.id);

                                  if (val && hasItemId) return;

                                  if (val) {
                                    setOpenedRows([...openedRows, x.id!]);

                                    return;
                                  }

                                  setOpenedRows([...openedRows.filter((z) => z != x.id)]);
                                }}
                              />
                            </td>
                          </tr>
                          {/* Mobile */}
                          <tr className={clsx(styles.mainRowMobile)}>
                            <td className={styles.mobileHeader}>
                              <div className={styles.mobileDesc}>{watch(`items.${index}.description`)}</div>
                              <CollapseBtnComponent
                                isShow={openedRows.some((z) => z == x.id)}
                                setIsShow={(val) => {
                                  const hasItemId = openedRows.some((z) => z == x.id);

                                  if (val && hasItemId) return;

                                  if (val) {
                                    setOpenedRows([...openedRows, x.id!]);

                                    return;
                                  }

                                  setOpenedRows([...openedRows.filter((z) => z != x.id)]);
                                }}
                              />
                            </td>
                            {/* <td></td> */}
                            <td className="d-flex">
                              <span className="me-3 fw-bold text-muted">Desc</span>
                              <input
                                type="text"
                                className="form-control form-control-solid fs-7"
                                {...register(`items.${index}.description`)}
                                disabled={!isEditMode}
                              />
                            </td>

                            <td className="d-sm-flex d-block">
                              <div className="d-flex w-100 align-items-center">
                                <span className="me-3 fw-bold text-muted">Type</span>
                                <select
                                  className="form-select form-select-solid fs-7"
                                  data-placeholder="Select option"
                                  data-allow-clear="true"
                                  data-hide-search="true"
                                  onChange={(e) =>
                                    setValue(
                                      `items.${index}.serviceType`,
                                      e.target.value ? Number(e.target.value) : undefined
                                    )
                                  }
                                  value={watch(`items.${index}.serviceType`) ?? ''}
                                  disabled={!isEditMode}
                                >
                                  <option value="">Select</option>
                                  {RepairOrderJobServiceTypeValues.map((x) => (
                                    <option key={x.value} value={x.value}>
                                      {x.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="d-flex w-100 align-items-center mt-2 mt-sm-0">
                                <span className="me-2 ms-sm-3 ms-0 fw-bold text-muted">Payer</span>
                                <select
                                  className="form-select form-select-solid fs-7"
                                  data-placeholder="Select option"
                                  data-allow-clear="true"
                                  data-hide-search="true"
                                  onChange={(e) =>
                                    setValue(
                                      `items.${index}.linePayer`,
                                      e.target.value ? Number(e.target.value) : undefined
                                    )
                                  }
                                  value={watch(`items.${index}.linePayer`) ?? ''}
                                  disabled={!isEditMode}
                                >
                                  <option value="">Select</option>
                                  {RepairOrderJobLinePayerValues.map((x) => (
                                    <option key={x.value} value={x.value}>
                                      {x.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </td>
                            <td className="d-flex">
                              <span className="me-4 fw-bold text-muted">Labours</span>{' '}
                              <span className={styles.spanText}>{getCountLabours(index)}</span>
                              <span className="me-4 ms-4 fw-bold text-muted">Parts</span>
                              <span className={styles.spanText}>{getCountParts(index)}</span>
                              <span className="me-4 ms-4 fw-bold text-muted">Time</span>
                              <span className={styles.spanText}>{Number(getSumMinutes(index) / 60).toFixed(2)}</span>
                            </td>
                            <td className="d-flex">
                              <span className="me-4 fw-bold text-muted">Total Price (incl VAT)</span>
                              <span className={styles.spanText}>{formatCurrencySafe(getTotal(index))}</span>
                            </td>
                            <td className="d-flex">
                              <select
                                className={clsx(
                                  styles.whiteDropdown,
                                  'form-select form-select-solid w-50 bg-secondary text-white',
                                  getStatusStyle(watch(`items.${index}.status`) ?? 0)
                                )}
                                data-placeholder="Select option"
                                data-allow-clear="true"
                                data-hide-search="true"
                                onChange={(e) =>
                                  handleChangeStatus(
                                    index,
                                    e.target.value !== undefined &&
                                      e.target.value !== null &&
                                      e.target.value.trim() !== ''
                                      ? Number(e.target.value)
                                      : -1
                                  )
                                }
                                value={watch(`items.${index}.status`) ?? ''}
                                disabled={isLoadingStatus || isLoading || isLoadingSave}
                              >
                                {RepairOrderQuoteStatusEnumValues.map((x) => (
                                  <option key={x.value} value={x.value} className={getStatusStyle(x.value)}>
                                    {x.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                          </tr>

                          <tr
                            className={clsx(
                              styles.detailsRow,
                              openedRows.some((z) => z == x.id) ? 'd-table-row' : 'd-none'
                            )}
                          >
                            <td colSpan={9}>
                              <div className={clsx(styles.detailContainer)}>
                                <div className={styles.tableContainer}>
                                  <LaboursTable mainIndex={index} />
                                </div>
                                <div className={styles.tableContainer}>
                                  <PartsTable mainIndex={index} isShow={openedRows.some((z) => z == x.id)} />
                                </div>
                                <div
                                  className={clsx(
                                    styles.footer,
                                    'mt-8 d-flex w-100 justify-content-between flex-nowrap w-100'
                                  )}
                                >
                                  <div className="d-block d-sm-flex justify-content-between w-100  flex-nowrap align-items-center gap-10">
                                    <div className={styles.btnContainer}>
                                      <div className={styles.btnCreateContainer}>
                                        <button
                                          type={'button'}
                                          className={clsx(
                                            styles.quoteBtn,
                                            'btn btn-primary text-nowrap w-30vw w-sm-100%'
                                          )}
                                          disabled={
                                            !isEditMode ||
                                            watch(`items.${index}.status`) !== RepairOrderQuoteStatusEnum.Created
                                          }
                                          onClick={() => handleSaveQuote(index)}
                                        >
                                          {!isEditMode ? 'Quote Created' : 'Save Changes'}
                                        </button>
                                        <div className={clsx('mt-2', styles.createdInfo)}>
                                          {dataState && dataState[index].dateCreated?.length ? (
                                            <>
                                              {formatDate(dataState[index].dateCreated!)} <br />
                                              by {dataState[index].userCreatedName ?? '-'}
                                            </>
                                          ) : null}
                                        </div>
                                      </div>
                                      <div className={styles.btnSubmitCDKContainer}>
                                        <button
                                          onClick={() => handleSubmitCDK(x.id!, index)}
                                          className={clsx(
                                            styles.quoteBtn,
                                            'btn btn-primary w-30vw w-sm-100% text-nowrap'
                                          )}
                                          type="button"
                                          disabled={
                                            !x.id ||
                                            isLoadingCdk ||
                                            (watch(`items.${index}.isSubmittedToCDK`) ?? false) ||
                                            !canSubmitToCdk(watch(`items.${index}.status`))
                                          }
                                        >
                                          {watch(`items.${index}.isSubmittedToCDK`) ? 'CDK Submitted' : 'Submit CDK'}
                                        </button>
                                        <div className={clsx('mt-2', styles.createdInfo)}>
                                          {dataState && dataState[index].dateSubmittedToCDK ? (
                                            <>
                                              {formatDate(dataState[index].dateSubmittedToCDK!)}
                                              <br />
                                              by {dataState[index].userSubmittedToCDK ?? '-'}
                                            </>
                                          ) : null}
                                        </div>
                                      </div>
                                    </div>
                                    <p className={styles.totalPrice}>
                                      Total Price (incl VAT) {'  '}{' '}
                                      <b className="fs-3">{formatCurrencySafe(getTotal(index))}</b>
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-2">
                  <ButtonsGroup
                    soHeaderId={soHeaderId}
                    checkResult={checkResult}
                    dateCompleted={dateCompleted}
                    hasQuote
                    dateSentAuthorizationRequiredEmail={dateSentAuthorizationRequiredEmail}
                    userSentAuthorizationRequiredEmail={userSentAuthorizationRequiredEmail}
                    dateSentAuthorizationRequiredSms={dateSentAuthorizationRequiredSms}
                    userSentAuthorizationRequiredSms={userSentAuthorizationRequiredSms}
                    refetch={refetchVhcRecord}
                  />
                </div>
              </div>

              {(isFetching || isLoading || isLoadingSave) && (
                <>
                  <div className={clsx(styles.overlay)}></div>
                  <Loading />
                </>
              )}
            </div>
          </form>
        </FormProvider>
      </div>
    </>
  );
};
